import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";

const rs = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

const STATUS_LABEL = {
  pending: "pending",
  hold: "on-hold",
  accepted: "accepted",
};

// Shown when saving the tenant's profile would break the rules of a property
// they have already requested or booked. `bookings` is the server's
// `affectedBookings` list — every figure in here comes from it, nothing is
// worked out in the browser.
//
// Three kinds of entry:
//   * not paid yet         -> the request will be withdrawn
//   * paid online          -> booking cancelled, refund shown per the ladder
//   * paid in cash         -> booking cancelled, and nothing about the cash: it
//                             never reached OTS. The one exception is the OTS
//                             service fee, if an admin already verified it —
//                             that is OTS's money, so its refund is shown.
const BookingCancellationDialog = ({ open, bookings, submitting, onKeep, onConfirm }) => {
  const anyPaid = bookings.some((b) => b.paid);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onKeep} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {anyPaid ? "This change will cancel your booking" : "This change will cancel your request"}
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your updated profile no longer matches the property's requirements.
          Because of this change, the {bookings.length > 1 ? "requests" : "request"} below will be
          cancelled. Are you sure you want to continue?
        </Alert>

        {bookings.map((b, index) => (
          <Box key={b.requestId} sx={{ mb: index === bookings.length - 1 ? 0 : 2 }}>
            {index > 0 && <Divider sx={{ mb: 2 }} />}

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {b.flatName || "Property"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {b.paid
                ? "Booking will be cancelled"
                : `Your ${STATUS_LABEL[b.status] || ""} request will be withdrawn`}
            </Typography>

            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: b.paid && b.refund ? 1.5 : 0 }}>
              {(b.reasons || []).map((reason) => (
                <li key={reason}>
                  <Typography variant="body2">{reason}</Typography>
                </li>
              ))}
            </Box>

            {b.paid && b.refund && (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                {b.refund.feeOnly && (
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    OTS service fee refund
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {b.refund.bandLabel} · {b.refund.percent}% refund
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                  <Typography variant="body2">
                    {b.refund.feeOnly ? "You paid OTS (service fee)" : "You paid"}
                  </Typography>
                  <Typography variant="body2">{rs(b.refund.paidAmount)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Deducted</Typography>
                  <Typography variant="body2">{rs(b.refund.deductedAmount)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    You get back
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {rs(b.refund.refundAmount)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ))}

        {anyPaid && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            This cannot be undone. The property becomes available to other tenants.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onKeep} disabled={submitting}>
          No, keep my profile as it is
        </Button>
        <Button
          onClick={onConfirm}
          disabled={submitting}
          variant="contained"
          color="error"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {anyPaid ? "Yes, update profile & cancel booking" : "Yes, update profile & cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingCancellationDialog;
